import { Accessor, createSignal } from 'solid-js';

import { PopoverAPI } from './Popover';

/**
 * Hook for managing a single popover ref
 * @returns [setRef, close] - setRef to pass to Popover's ref prop, close to close the popover
 */
export function usePopoverRef(): [(api: PopoverAPI) => void, () => void] {
  let popoverRef: PopoverAPI | undefined;

  const setRef = (api: PopoverAPI) => {
    popoverRef = api;
  };

  const close = () => {
    if (popoverRef) {
      popoverRef.close();
    }
  };

  return [setRef, close];
}

/**
 * Hook for managing multiple popover refs (e.g., in a loop/Index)
 * @returns [setRef, close, removeRef] - setRef(index) to pass to Popover's ref prop, close(index) to close a specific popover, removeRef(index) to cleanup when an item is removed
 */
export function useIndexedPopoverRefs(): [
  (index: number) => (api: PopoverAPI) => void,
  (index: number) => void,
  (index: number) => void,
] {
  const refs = new Map<number, PopoverAPI>();

  const setRef = (index: number) => (api: PopoverAPI) => {
    refs.set(index, api);
  };

  const close = (index: number) => {
    const ref = refs.get(index);
    if (ref) {
      ref.close();
    }
  };

  const removeRef = (index: number) => {
    refs.delete(index);
  };

  return [setRef, close, removeRef];
}

export type IndexedPopoverState = {
  /** Which row index currently has its popover open (null if none). */
  openIndex: Accessor<number | null>;
  /**
   * Mark a row's popover as open. If a `triggerSelector` was provided, it also
   * programmatically opens the popover by dispatching a `mousedown` on its
   * trigger button (used when opening is driven by something other than the
   * trigger itself, e.g. a checkbox).
   */
  open: (index: number) => void;
  /** Reactively close whichever popover is open. */
  close: () => void;
  /** `onClick` handler for the Popover trigger — keeps state in sync on manual opens. */
  onTriggerClick: (index: number) => () => void;
  /** Value for the Popover `triggerClose` prop for a given row. */
  triggerClose: (index: number) => boolean;
};

/**
 * Manages open/close state for popovers rendered in a loop and closes them
 * reactively via the Popover `triggerClose` prop (no imperative refs needed).
 *
 * @param triggerSelector optional CSS-selector builder pointing at the row
 *   wrapper whose trigger `button` should receive a synthetic `mousedown` when
 *   `open(index)` is called (e.g. `(i) => \`[data-hazmat-row-index="${i}"]\``).
 */
export function useIndexedPopoverState(
  triggerSelector?: (index: number) => string,
): IndexedPopoverState {
  const [openIndex, setOpenIndex] = createSignal<number | null>(null);

  const open = (index: number) => {
    setOpenIndex(index);
    if (!triggerSelector) return;
    const triggerButton = document.querySelector(
      `${triggerSelector(index)} button`,
    );
    if (triggerButton instanceof HTMLElement) {
      triggerButton.dispatchEvent(
        new MouseEvent('mousedown', { bubbles: true, cancelable: true }),
      );
    }
  };

  return {
    openIndex,
    open,
    close: () => setOpenIndex(null),
    onTriggerClick: (index: number) => () => setOpenIndex(index),
    triggerClose: (index: number) => openIndex() !== index,
  };
}

export type PopoverState = {
  /** Whether the popover is currently open. */
  isOpen: Accessor<boolean>;
  /** Mark the popover as open. */
  open: () => void;
  /** Reactively close the popover. */
  close: () => void;
  /** `onClick` handler for the Popover trigger. */
  onTriggerClick: () => void;
  /** Value for the Popover `triggerClose` prop. */
  triggerClose: () => boolean;
};

/**
 * Single-popover variant of {@link useIndexedPopoverState}. Drives the Popover
 * `triggerClose` prop reactively instead of using an imperative ref.
 */
export function usePopoverState(): PopoverState {
  const [isOpen, setIsOpen] = createSignal(false);

  return {
    isOpen,
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    onTriggerClick: () => setIsOpen(true),
    triggerClose: () => !isOpen(),
  };
}
